import { DATE_FORMAT, getDateTimeFormat, getTimeFormat, TimeFormat } from '@chronosphereio/core';
import { Box, Stack } from '@mui/material';
import { DateField, DateTimeField, DesktopTimePicker } from '@mui/x-date-pickers-pro';
import { Calendar, MenuDown, MenuUp } from 'mdi-material-ui';
import { useState } from 'react';
import { SxProps } from '../../../utils';

export interface DateTimeInputFieldsProps {
  onChange: (date: Date | null) => void;
  timeFormat: TimeFormat;
  value: Date | null;
  combineInput?: boolean;
  hideDate?: boolean;
  hideTime?: boolean;
  label?: string;
  maxTime?: Date;
  minTime?: Date;
  sx?: SxProps;
}

export const DateTimeInputFields = ({
  label,
  value,
  onChange,
  minTime,
  maxTime,
  sx,
  combineInput,
  hideDate,
  hideTime,
  timeFormat,
}: DateTimeInputFieldsProps) => {
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  return (
    <Box sx={sx}>
      {label && !combineInput && (
        <Box sx={(theme) => ({ color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5) })}>{label}</Box>
      )}
      {combineInput ? (
        <DateTimeField
          label={label}
          value={value}
          onChange={onChange}
          format={getDateTimeFormat({ timeFormat })}
          minDateTime={minTime}
          maxDateTime={maxTime}
          inputProps={{
            'aria-label': `${label} date time`,
          }}
          sx={{ width: '100%' }}
        />
      ) : (
        <Stack direction="row" sx={(theme) => ({ gap: theme.spacing(1) })}>
          {!hideDate && (
            <DateField
              value={value}
              onChange={onChange}
              format={DATE_FORMAT}
              InputProps={{
                startAdornment: (
                  <Calendar
                    sx={(theme) => ({
                      color: theme.palette.text.disabled,
                      width: '18px',
                      height: '18px',
                      margin: '2px 8px 2px 0px',
                    })}
                  />
                ),
              }}
              minDate={minTime}
              maxDate={maxTime}
              inputProps={{
                'aria-label': `${label} date`,
              }}
              sx={{ width: '160px' }}
            />
          )}
          {!hideTime && (
            <DesktopTimePicker
              ampm={timeFormat === '12h'}
              open={timePickerOpen}
              onOpen={() => setTimePickerOpen(true)}
              onClose={() => setTimePickerOpen(false)}
              value={value}
              onChange={onChange}
              format={getTimeFormat({ timeFormat })}
              views={['hours', 'minutes', 'seconds']}
              timeSteps={{ hours: 1, minutes: 15, seconds: 15 }}
              sx={{ width: '150px' }}
              slots={{
                openPickerIcon: timePickerOpen ? MenuUp : MenuDown,
              }}
              slotProps={{
                digitalClockSectionItem: {
                  sx: {
                    height: '40px', // fixes aligning selected item to the top of the scrollable
                  },
                },
                textField: {
                  inputProps: {
                    'aria-label': `${label} time`,
                  },
                },
              }}
              disableIgnoringDatePartForTimeValidation
              minTime={minTime}
              maxTime={maxTime}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};
